import { createContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const defaultPromoCodeOrganic = 90891;
const defaultPromoCodeAd = 91147;
const defaultRAFPromoCode = 90948;

const PromotionContext = createContext({
  promo: defaultPromoCodeOrganic,
  RAF: null,
  defaultRAFPromoCode,
  contentType: null,
  affiliate: null,
});

// Get the current RAF value from local storage, unless it is expired
const getRAF = () => {
  try {
    const raf = window.localStorage.getItem('RAF');
    const { value, expires } = JSON.parse(raf);
    if (Date.now() > expires) {
      window.localStorage.removeItem('RAF');
      return null;
    }
    return value;
  } catch (e) {
    return null;
  }
};

// Set the current RAF value with an expiry time of 2 hours in local storage
const storeRAF = value => {
  try {
    const twoHours = 1000 * 60 * 60 * 2;
    const expires = Date.now() + twoHours;
    const raf = JSON.stringify({ value, expires });
    window.localStorage.setItem('RAF', raf);
  } catch (e) {
    console.warn(e);
  }
};

// Set the current affiliate value in session storage
const storeAffiliate = value => {
  try {
    const affiliate = JSON.stringify({ value });
    window.sessionStorage.setItem('affiliate', affiliate);
  } catch (e) {
    console.warn(e);
  }
};

// Get the current affiliate value from session storage
const getAffiliate = () => {
  try {
    const affiliate = window.sessionStorage.getItem('affiliate');
    const { value } = JSON.parse(affiliate);
    return value;
  } catch (e) {
    return null;
  }
};

const Promotion = ({
  children,
  value = defaultPromoCodeOrganic,
  location = null,
  contentType = null,
}) => {
  const defaultPromoCode = value || defaultPromoCodeOrganic;
  const [promoCode, setPromoCode] = useState(defaultPromoCode);
  const [RAF, setRAF] = useState(null);
  const [affiliate, setAffiliate] = useState(null);
  const affiliateList = ['gclid', 'ckanonid', 'rhubid', 'gcanid'];
  const changeSearchParamsToArrayObject = entries => {
    const result = [];
    entries.forEach(entry => {
      if (affiliateList.includes(entry[0])) {
        result.push({
          affiliateCode: entry[0],
          affiliateId: entry[1],
        });
      }
    });
    return result;
  };

  // * When the page first mounts we check for a filterQuery in the url
  useEffect(() => {
    if (!value && typeof window !== 'undefined') {
      if (
        location.search.indexOf('utm_medium=paidsearch') > -1 ||
        location.search.indexOf('utm_medium=cpc') > -1 ||
        window.sessionStorage.getItem('paid_search')
      ) {
        // promoCode = defaultPromoCodeAd;
        setPromoCode(defaultPromoCodeAd);
        window.sessionStorage.setItem('paid_search', 'true');
      }
    }
    // * Gets RAF code from window location
    if (typeof document !== 'undefined') {
      const urlSearchParams = new URLSearchParams(document.location.search);
      const formattedParams = changeSearchParamsToArrayObject(
        Array.from(urlSearchParams.entries()),
      );
      const queryRafParam = urlSearchParams.get('referralCode');
      // If query parameter exists, set RAF to query parameter and set stored RAF immediately
      if (queryRafParam) {
        setRAF(queryRafParam);
        storeRAF(queryRafParam);
      } else {
        setRAF(getRAF());
      }

      // setting afflifiate codes depending if formatted params is populated
      const lengthOfFormattedParams = formattedParams.length;
      if (lengthOfFormattedParams > 0) {
        // take the last occurring affiliate
        setAffiliate(formattedParams[lengthOfFormattedParams - 1]);
        storeAffiliate(formattedParams[lengthOfFormattedParams - 1]);
      } else {
        setAffiliate(getAffiliate);
      }
    }
  }, []);

  const promotionContextValues = useMemo(
    () => ({
      promo: promoCode,
      RAF,
      defaultRAFPromoCode,
      contentType,
      affiliate,
    }),
    [promoCode, affiliate, RAF],
  );

  return (
    <PromotionContext.Provider value={promotionContextValues}>
      {children}
    </PromotionContext.Provider>
  );
};

Promotion.propTypes = {
  location: PropTypes.shape(),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  value: PropTypes.string,
  contentType: PropTypes.string,
};

export { Promotion, PromotionContext };
